import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Modal, Spinner, Table } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import { PartyBadge } from '../components/PartyBadge';
import { adminApi, extractError } from '../api/client';
import { useToast } from '../contexts/ToastContext';

// Mirror of backend's normalize_party_label() — short labels for badges.
function normalizeParty(party) {
  if (!party) return '';
  const p = String(party).toLowerCase();
  if (p.includes('dem')) return 'DEM';
  if (p.includes('rep') || p.includes('gop')) return 'REP';
  if (p.includes('lib')) return 'LIB';
  if (p.includes('gre')) return 'GRE';
  if (p.includes('npa') || p.includes('no party') || p.includes('unaffiliated')) return 'NPA';
  if (p.includes('ind')) return 'IND';
  if (p.includes('wri')) return 'WRI';
  return 'OTHER';
}

export default function StagingInboxPage() {
  const toast = useToast();
  const [adapters, setAdapters] = useState([]);
  const [adapterFilter, setAdapterFilter] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [applying, setApplying] = useState(false);
  const [showRunModal, setShowRunModal] = useState(false);
  const [dataSources, setDataSources] = useState([]);
  const [runAdapter, setRunAdapter] = useState('');
  const [runDataSource, setRunDataSource] = useState('');
  const [running, setRunning] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.get('/admin/adapters');
        setAdapters(r.data.items || []);
      } catch (_e) {}
    })();
  }, []);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ status: 'pending', page_size: '200' });
      if (adapterFilter) params.set('adapter_name', adapterFilter);
      const r = await adminApi.get(`/admin/staging-rows?${params.toString()}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setSelected(new Set());
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [adapterFilter]);

  function toggle(id) {
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  async function applySelected() {
    if (selected.size === 0) { toast.error('Select rows first'); return; }
    setApplying(true);
    try {
      const r = await adminApi.post('/admin/staging-rows/bulk-apply', { row_ids: Array.from(selected) });
      toast.success(`Applied ${r.data.applied} row(s)${r.data.failed?.length ? `, ${r.data.failed.length} failed` : ''}`);
      load();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setApplying(false);
    }
  }

  async function openRunModal() {
    setRunAdapter(adapters[0]?.name || '');
    try {
      const r = await adminApi.get('/admin/states/florida/data-sources?page_size=20').catch(() => null);
      // fallback: list all data_sources via filter — use a generic endpoint
      const r2 = await adminApi.get('/admin/data-sources?page_size=100').catch(() => ({ data: { items: [] } }));
      setDataSources(r2.data.items || []);
    } catch (_e) { setDataSources([]); }
    setShowRunModal(true);
  }

  async function runNow() {
    if (!runAdapter || !runDataSource) { toast.error('Pick adapter and data source'); return; }
    setRunning(true);
    try {
      const r = await adminApi.post(`/admin/adapters/${runAdapter}/run?data_source_id=${runDataSource}`);
      toast.success(`Fetched ${r.data.fetched}, staged ${r.data.staged}`);
      setShowRunModal(false);
      load();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Staging Inbox"
        subtitle="Review records discovered by source adapters before they land in races. Apply to commit, ignore to leave pending."
        actions={
          <div className="d-flex gap-2">
            <Button variant="outline-primary" onClick={openRunModal} data-testid="run-adapter-button"><i className="bi bi-cloud-download me-1" />Run adapter</Button>
            <Button variant="primary" onClick={applySelected} disabled={selected.size === 0 || applying} data-testid="apply-selected-button">
              {applying ? <Spinner size="sm" animation="border" /> : `Apply selected (${selected.size})`}
            </Button>
          </div>
        }
      />
      <div className="page-content">
        {error && <Alert variant="danger">{error}</Alert>}
        <Card className="mb-3">
          <Card.Body>
            <div className="d-flex gap-2 flex-wrap">
              <div style={{ minWidth: 240 }}>
                <Form.Label className="text-muted" style={{ fontSize: '0.78rem' }}>Adapter</Form.Label>
                <Form.Select value={adapterFilter} onChange={(e) => setAdapterFilter(e.target.value)} data-testid="staging-adapter-filter">
                  <option value="">All adapters</option>
                  {adapters.map((a) => <option key={a.name} value={a.name}>{a.label}</option>)}
                </Form.Select>
              </div>
            </div>
          </Card.Body>
        </Card>

        <Card>
          {loading ? (
            <div className="text-center p-5"><Spinner animation="border" /></div>
          ) : items.length === 0 ? (
            <div className="empty-state"><i className="bi bi-inbox" /><div>No pending rows.</div></div>
          ) : (
            <Table size="sm" responsive hover className="mb-0" data-testid="staging-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <Form.Check checked={selected.size === items.length && items.length > 0} onChange={(e) => setSelected(e.target.checked ? new Set(items.map((i) => i.id)) : new Set())} />
                  </th>
                  <th>Source</th>
                  <th>Entity</th>
                  <th>Party</th>
                  <th>Details</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} data-testid={`staging-row-${row.id}`}>
                    <td><Form.Check checked={selected.has(row.id)} onChange={() => toggle(row.id)} data-testid={`staging-row-check-${row.id}`} /></td>
                    <td>
                      <Badge bg="secondary">{row.adapter_name || row.target_type}</Badge>
                      <div className="text-muted" style={{ fontSize: '0.75rem' }}><code>{row.source_record_id}</code></div>
                    </td>
                    <td><Badge bg="light" text="dark">{row.entity_type || row.target_type}</Badge></td>
                    <td data-testid={`staging-party-${row.id}`}>
                      {(() => {
                        // Prefer the normalized payload party (set by every
                        // adapter); fall back to raw payload for older rows.
                        const raw = row.payload?.party || row.raw?.party_full || row.raw?.party || '';
                        const label = normalizeParty(raw);
                        return label ? <PartyBadge party={label} title={raw} /> : <span className="text-muted small">—</span>;
                      })()}
                      {row.payload?.fec_party_short && (
                        <div className="text-muted small">{row.payload.fec_party_short}</div>
                      )}
                      {row.raw?.binding && (
                        <div className="text-muted small">
                          <i className="bi bi-link-45deg" /> {row.raw.binding}
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>
                      <strong>{row.raw?.name}</strong>
                      <div className="text-muted">
                        {row.raw?.office === 'H' && row.raw?.state ? `US House ${row.raw.state}-${row.raw.district}` : ''}
                        {row.raw?.office === 'S' && row.raw?.state ? `US Senate ${row.raw.state}` : ''}
                        {row.raw?.office === 'P' ? 'US President' : ''}
                        {row.raw?.chamber && row.raw?.district ? `${row.raw.chamber === 'lower' ? 'State House' : 'State Senate'} District ${row.raw.district}` : ''}
                      </div>
                    </td>
                    <td className="text-muted" style={{ fontSize: '0.8rem' }}>{(row.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
        <div className="text-muted mt-2" style={{ fontSize: '0.82rem' }}>{total} pending row{total === 1 ? '' : 's'}.</div>

        <Modal show={showRunModal} onHide={() => setShowRunModal(false)} centered>
          <Modal.Header closeButton><Modal.Title>Run adapter now</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-2">
              <Form.Label>Adapter</Form.Label>
              <Form.Select value={runAdapter} onChange={(e) => setRunAdapter(e.target.value)} data-testid="run-adapter-select">
                {adapters.map((a) => <option key={a.name} value={a.name}>{a.label}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Data source</Form.Label>
              <Form.Select value={runDataSource} onChange={(e) => setRunDataSource(e.target.value)} data-testid="run-datasource-select">
                <option value="">— Select —</option>
                {dataSources.filter((d) => !runAdapter || d.adapter_name === runAdapter || d.source_type === 'adapter').map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </Form.Select>
              <Form.Text>FEC sources require a state-scoped data source with adapter_name=fec_candidates and config.cycle.</Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowRunModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={runNow} disabled={running || !runDataSource} data-testid="run-now-button">
              {running ? <Spinner size="sm" animation="border" /> : 'Run now'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
}
